import { AttachMoney } from '@mui/icons-material';
import { FormControl, FormLabel, Grid, TextField, Typography } from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { MiscLineItemDto, OrderDto, QuoteDto, RegionDto } from '../../../../../dtos';
import { useArchiveLineItemByIdMutation, useArchiveMiscLineItemDeleteFromDatabaseByIdMutation, useGetLineItemHasBeenOrderedQuery, usePatchLineItemByIdMutation } from '../../../../../store/generated/generatedApi';
import { EquipmentTypeSelect, SiteSelect, UnitOfMeasureSelect, PoAndJobNumberInput, AddressForm } from '../../../../CommonInputs';
import { SlimFormHeader } from '../../../../Core/SlimFormHeader';
import { FormInput, FormNumberInput, FormSection, IFormProps, LoadingIndicator } from '../../../../CoreLib/library';
import { useMiscLineItemForm } from './useMiscLineItemForm';
import { AddressLookup } from '../../../../CommonInputs/AddressLookup';
import { IEntityAutocomplete } from '../../../../CommonInputs/IEntityAutocomplete';

export interface IMiscLineItemFormProps extends IFormProps<MiscLineItemDto> {
    quote?: QuoteDto;
    order?: OrderDto;
}

export const MiscLineItemForm: FC<IMiscLineItemFormProps> = (props) => {
    const { isLoading, quote, order } = props;
    const {
        isFormDirty,
        handleSave,
        handleCancel,
        fieldErrors,
        handleQuoteChange,
        handleEquipmentTypeChange,
        handleSiteChange,
        handleRateChange,
        handleCostChange,
        handleQuantityChange,
        handleDescriptionChange,
        handleLineItemMemoChange,
        handleDriverMemoChange,
        handleUnitOfMeasureChange,
        handleCustomerJobNumberChange,
        handlePoNumberChange,
        handleIsCustomPoNumberChange,
        handleIsCustomerJobNumberCustomChange,
        handleFormAddressChange,
        handleFormRegionChange,
        formQuote,
        formEquipmentType,
        formSite,
        formSiteName,
        formRate,
        formCost,
        formQuantity,
        formDescription,
        formLineItemMemo,
        formDriverMemo,
        formUnitOfMeasure,
        formIsActive,
        formTotal,
        formCustomerJobNumber,
        formPoNumber,
        formIsCustomPoNumber,
        formIsCustomerJobNumberCustom,
        formQuotePoNumber,
        formQuoteCustomerJobNumber,
        formAddressLine1,
        formAddressLine2,
        formCity,
        formState,
        formZipCode,
        formCountry,
        formDirections,
        formRegion,
        regions,
    } = useMiscLineItemForm(props, quote);

    const { data: canDeleteData, error: canDeleteError, isLoading: canDeleteLoading } = useGetLineItemHasBeenOrderedQuery({ id: props.initValues?.id ?? '' });

    const [canDelete, setCanDelete] = useState(true);
    useEffect(() => {
        if (canDeleteError) {
            if ((canDeleteError as any).status === 404) {
                setCanDelete(true);
            }
        }
        if (canDeleteData && canDeleteData === true) {
            setCanDelete(false);
        }
    }, [canDeleteData, canDeleteError]);

    useEffect(() => {
        if (quote && !formQuote) {
            handleQuoteChange(quote);
        }
    }, [formQuote, handleQuoteChange, quote]);

    const currentMiscLineItemId = useMemo(() => {
        return props.initValues?.id;
    }, [props.initValues?.id]);

    if (isLoading || canDeleteLoading) {
        return <LoadingIndicator />;
    }

    return (
        <Grid container direction='column'>
            <SlimFormHeader
                objectName='Misc Line Item'
                permissionsTypeName={'lineItem'}
                icon={<AttachMoney />}
                title={formQuote ? `Misc. for Quote #${formQuote.quoteNumber}` : 'New Misc. Line Item'}
                titleMaxWidth="60vw"
                breadcrumbs={
                    order
                        ? [
                            { label: 'Home', navLink: '/' },
                            { label: 'Jobs', navLink: '/jobs' },
                            { label: `Job #${quote?.quoteNumber}`, navLink: `/job/${order?.jobId}` },
                            { label: `Order #${order?.orderNumber}`, navLink: `/job/${order?.jobId}/order/${order?.id}` },
                        ]
                        : [
                            { label: 'Home', navLink: '/' },
                            { label: 'Quotes', navLink: '/quotes' },
                            { label: `${quote?.jobName ? `${quote.jobName} - ` : ''}${quote?.customer?.customerNumber ? `${quote.customer.customerNumber}: ` : ''}${quote?.customer?.name ?? ''} (Quote #${quote?.quoteNumber ?? 'Quote'})`, navLink: `/quote/${quote?.id}` },
                            { label: `Line Items`, navLink: `/quote/${quote?.id}/lineItems` },
                        ]
                }
                isActive={formIsActive}
                id={currentMiscLineItemId}
                isFormDirty={isFormDirty}
                handleCancel={handleCancel}
                handleSave={handleSave}
                entityNameSingular={'misc line item'}
                deleteMutation={useArchiveMiscLineItemDeleteFromDatabaseByIdMutation}
                canSave={true}
                canDelete={canDelete}
                archiveRestoreProps={{
                    restoreMutation: usePatchLineItemByIdMutation,
                    archiveMutation: useArchiveLineItemByIdMutation,
                    isActive: formIsActive,
                }}
            />
            <Grid item mx={2}>
                <FormSection>
                    <Grid item>
                        <Typography variant='h2'>General</Typography>
                    </Grid>
                    <Grid item container direction='row' spacing={3}>
                        <Grid item xs={12} sm={6} md={5} lg={4} xl={3}>
                            <EquipmentTypeSelect
                                selectedEquipmentTypeId={formEquipmentType?.id ?? ''}
                                handleSelectedEquipmentTypeChange={handleEquipmentTypeChange}
                                required
                                errorMessage={fieldErrors.get('equipmentTypeId')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={7} lg={8} xl={9}>
                            <FormInput
                                value={formDescription}
                                onChange={handleDescriptionChange}
                                label='Description'
                                name='description'
                                errorText={fieldErrors.get('description')}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <PoAndJobNumberInput
                                formQuotePoNumber={formQuotePoNumber}
                                formIsCustomPoNumber={formIsCustomPoNumber}
                                handleIsCustomPoNumberChange={handleIsCustomPoNumberChange}
                                formPoNumber={formPoNumber}
                                formQuoteCustomerJobNumber={formQuoteCustomerJobNumber}
                                formIsCustomerJobNumberCustom={formIsCustomerJobNumberCustom}
                                formCustomerJobNumber={formCustomerJobNumber}
                                handleIsCustomerJobNumberCustomChange={handleIsCustomerJobNumberCustomChange}
                                handleCustomerJobNumberChange={handleCustomerJobNumberChange}
                                handlePoNumberChange={handlePoNumberChange}
                                fieldErrors={fieldErrors}
                            />
                        </Grid>
                    </Grid>
                    <Grid item>
                        <Typography variant='h2'>Pricing</Typography>
                    </Grid>
                    <Grid item container direction='row' spacing={3}>
                        <Grid item xs={12} sm={6} md={6} lg={2}>
                            <UnitOfMeasureSelect
                                selectedUnit={formUnitOfMeasure ?? ''}
                                handleSelectedUnitChange={handleUnitOfMeasureChange}
                                errorMessage={fieldErrors.get('unitOfMeasure')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={6} lg={2}>
                            <FormNumberInput
                                value={formCost}
                                onChange={handleCostChange}
                                label='Cost'
                                name='cost'
                                errorText={fieldErrors.get('cost')}
                                error={!!fieldErrors.get('cost')}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={2}>
                            <FormNumberInput
                                value={formQuantity ?? ''}
                                onChange={handleQuantityChange}
                                label='Quantity'
                                name='quantity'
                                errorText={fieldErrors.get('quantity')}
                                error={!!fieldErrors.get('quantity')}
                                fullWidth
                                disabled={isLoading}
                                inputProps={{ min: 0 }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={2}>
                            <FormNumberInput
                                value={formRate}
                                onChange={handleRateChange}
                                label='Rate'
                                name='Rate'
                                errorText={fieldErrors.get('rate')}
                                error={!!fieldErrors.get('rate')}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={4} lg={2}>
                            <FormNumberInput value={formTotal ?? ''} onChange={() => { }} label='Total' name='total' fullWidth disabled isCurrency />
                        </Grid>
                    </Grid>
                    <Grid item>
                        <Typography variant='h2'>Haul To</Typography>
                    </Grid>
                    <Grid item container direction='row' spacing={3}>
                        <Grid item xs={3}>
                            <AddressLookup
                                handleAddressChange={handleFormAddressChange}
                                key={0}
                                disabled={isLoading}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <SiteSelect
                                label='Site Lookup'
                                selectedSiteId={formSite?.id ?? ''}
                                handleSelectedSiteChange={handleSiteChange}
                                errorMessage={fieldErrors.get('siteId')}
                                disabled={isLoading}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormInput
                                value={formSiteName ?? ''}
                                onChange={(event) => { handleFormAddressChange({ ...{ siteName: event.target.value, addressLine1: formAddressLine1, addressLine2: formAddressLine2, city: formCity, state: formState, zipCode: formZipCode, country: formCountry, directions: formDirections } }) }}
                                label='Site Name'
                                name='siteName'
                                fullWidth
                                disabled={isLoading || !!formSite}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormControl fullWidth>
                                <FormLabel>Region</FormLabel>
                                <IEntityAutocomplete
                                    options={regions?.pageResults ?? []}
                                    onChange={(event, value) => handleFormRegionChange(value)}
                                    value={formRegion}
                                    getOptionLabel={(option: RegionDto) => option.name}
                                    disabled={isLoading}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item container direction={'row'} spacing={2} wrap='nowrap'>
                            <AddressForm
                                handleAddressChange={handleFormAddressChange}
                                addressLine1={formAddressLine1}
                                addressLine2={formAddressLine2}
                                city={formCity}
                                state={formState}
                                zipCode={formZipCode}
                                country={formCountry}
                                directions={formDirections}
                                enableLookup={false}
                                disabled={isLoading}
                                allOptional
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                value={formLineItemMemo ?? ''}
                                onChange={handleLineItemMemoChange}
                                label='Line Item Memo'
                                name='lineItemMemo'
                                fullWidth
                                multiline
                                minRows={3}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                value={formDriverMemo ?? ''}
                                onChange={handleDriverMemoChange}
                                label='Order Memo'
                                fullWidth
                                multiline
                                minRows={3}
                            />
                        </Grid>
                    </Grid>
                </FormSection>
            </Grid>
        </Grid>
    );
};
